/**
 * Global auto-scroll master switch.
 *
 * ONE preference that every auto-scroll behaviour in the app must consult:
 *   - LegalPractice source-paper Follow (`autoFollow` effect)
 *   - LiveHighlighter caret-into-view scroll (Dictation / Legal / Pitman / Typing)
 *   - Anything else future modules add
 *
 * Backed by localStorage so the choice persists across sessions and modules.
 * A CustomEvent lets React components re-render immediately when the toggle
 * flips — no full page reload, no prop-drilling.
 */
const KEY = "stenosync.globalAutoScroll.v1";
const EVENT = "stenosync-auto-scroll-changed";

export function isAutoScrollOn() {
  try { return localStorage.getItem(KEY) !== "off"; } catch { return true; }
}

export function setAutoScroll(on) {
  try { localStorage.setItem(KEY, on ? "on" : "off"); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: on })); } catch { /* ignore */ }
}

export function toggleAutoScroll() {
  const next = !isAutoScrollOn();
  setAutoScroll(next);
  return next;
}

// React hook — subscribes to the CustomEvent so consumers re-render.
import { useEffect, useState } from "react";
export function useAutoScrollPref() {
  const [on, setOn] = useState(isAutoScrollOn);
  useEffect(() => {
    const handler = (e) => setOn(!!e.detail);
    window.addEventListener(EVENT, handler);
    // Also react to storage changes from OTHER tabs so multi-tab practice stays in sync.
    const onStorage = (e) => { if (e.key === KEY) setOn(isAutoScrollOn()); };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return [on, (v) => setAutoScroll(!!v)];
}
